import { useCallback, useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import SubscriptIcon from "@mui/icons-material/Subscript";
import SuperscriptIcon from "@mui/icons-material/Superscript";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import { sanitizeInAppNotificationHtml } from "../../utils/inAppNotificationUtils";
import "./inAppRichTextEditor.css";

const TEXT_COLORS = [
  "#000000",
  "#FF0000",
  "#FF6600",
  "#FFD700",
  "#008000",
  "#0000FF",
  "#4B0082",
  "#800080",
  "#808080",
];

const BACKGROUND_COLORS = [
  "#FFFFFF",
  "#FFEB3B",
  "#FFE0B2",
  "#C8E6C9",
  "#BBDEFB",
  "#E1BEE7",
  "#F5F5F5",
  "#FFCDD2",
  "#B2DFDB",
];

const FONT_SIZES = [
  { label: "Small", value: "small" },
  { label: "Normal", value: "medium" },
  { label: "Large", value: "large" },
];

const FORMATTING_TAG_SELECTOR =
  "b, strong, i, em, u, del, ins, sub, sup, span, mark, small";

const unwrapFormattingTags = (container) => {
  [...container.querySelectorAll(FORMATTING_TAG_SELECTOR)].reverse().forEach((node) => {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    parent.removeChild(node);
  });
};

const focusEditor = (editorRef) => {
  editorRef.current?.focus();
};

const wrapSelection = (tagName, style) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return false;

  const range = selection.getRangeAt(0);
  if (range.collapsed) return false;

  const wrapper = document.createElement(tagName);
  if (style) {
    wrapper.setAttribute("style", style);
  }

  try {
    range.surroundContents(wrapper);
  } catch {
    const fragment = range.extractContents();
    wrapper.appendChild(fragment);
    range.insertNode(wrapper);
  }

  selection.removeAllRanges();
  const nextRange = document.createRange();
  nextRange.selectNodeContents(wrapper);
  selection.addRange(nextRange);
  return true;
};

const InAppRichTextEditor = ({
  value = "",
  onChange,
  height = 150,
  error = false,
  placeholder = "Enter description",
  id,
}) => {
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);
  const [textColor, setTextColor] = useState("#000000");
  const [backgroundColor, setBackgroundColor] = useState("#FFFFFF");
  const [fontSize, setFontSize] = useState("medium");
  const [showTextColors, setShowTextColors] = useState(false);
  const [showBackgroundColors, setShowBackgroundColors] = useState(false);

  const emitChange = useCallback(() => {
    if (!editorRef.current || !onChange) return;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const applyExecCommand = useCallback(
    (command, commandValue) => {
      focusEditor(editorRef);
      document.execCommand(command, false, commandValue ?? null);
      emitChange();
    },
    [emitChange],
  );

  const applySemanticTag = useCallback(
    (tagName) => {
      focusEditor(editorRef);
      if (!wrapSelection(tagName)) {
        document.execCommand("insertHTML", false, `<${tagName}></${tagName}>`);
      }
      emitChange();
    },
    [emitChange],
  );

  const applyInlineStyle = useCallback(
    (style) => {
      focusEditor(editorRef);
      if (!wrapSelection("span", style)) {
        document.execCommand("insertHTML", false, `<span style="${style}"></span>`);
      }
      emitChange();
    },
    [emitChange],
  );

  const applyTextColor = useCallback(
    (color) => {
      setTextColor(color);
      setShowTextColors(false);
      applyInlineStyle(`color:${color}`);
    },
    [applyInlineStyle],
  );

  const applyBackgroundColor = useCallback(
    (color) => {
      setBackgroundColor(color);
      setShowBackgroundColors(false);
      applyInlineStyle(`background-color:${color}`);
    },
    [applyInlineStyle],
  );

  const applyFontSize = useCallback(
    (size) => {
      setFontSize(size);
      applyInlineStyle(`font-size:${size}`);
    },
    [applyInlineStyle],
  );

  const applyRemoveFormatting = useCallback(() => {
    focusEditor(editorRef);
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    document.execCommand("removeFormat", false, null);

    const activeRange = selection.rangeCount ? selection.getRangeAt(0) : range;
    const selectedFragment = activeRange.cloneContents();
    const temp = document.createElement("div");
    temp.appendChild(selectedFragment);
    unwrapFormattingTags(temp);

    activeRange.deleteContents();
    const cleanedNodes = document.createDocumentFragment();
    while (temp.firstChild) {
      cleanedNodes.appendChild(temp.firstChild);
    }
    activeRange.insertNode(cleanedNodes);

    selection.removeAllRanges();
    emitChange();
  }, [emitChange]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (!editorRef.current || !onChange) return;

    const sanitized = sanitizeInAppNotificationHtml(editorRef.current.innerHTML);
    if (sanitized !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = sanitized;
    }
    onChange(sanitized);
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current || isFocusedRef.current) return;
    const nextValue = value || "";
    if (editorRef.current.innerHTML !== nextValue) {
      editorRef.current.innerHTML = nextValue;
    }
  }, [value]);

  const editorMinHeight = Math.max(height - 40, 80);

  return (
    <div>
      <div className="inapp-rt-toolbar">
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyExecCommand("bold")}
          title="Bold"
          aria-label="Bold"
        >
          <FormatBoldIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyExecCommand("italic")}
          title="Italic"
          aria-label="Italic"
        >
          <FormatItalicIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyExecCommand("underline")}
          title="Underline"
          aria-label="Underline"
        >
          <FormatUnderlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applySemanticTag("del")}
          title="Strikethrough"
          aria-label="Strikethrough"
        >
          <FormatStrikethroughIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applySemanticTag("ins")}
          title="Inserted"
          aria-label="Inserted"
          sx={{ fontSize: 11, fontWeight: 700, width: 32 }}
        >
          Ins
        </IconButton>
        <div className="inapp-rt-toolbar-divider" />
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyExecCommand("subscript")}
          title="Subscript"
          aria-label="Subscript"
        >
          <SubscriptIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => applyExecCommand("superscript")}
          title="Superscript"
          aria-label="Superscript"
        >
          <SuperscriptIcon fontSize="small" />
        </IconButton>
        <div className="inapp-rt-toolbar-divider" />
        <ClickAwayListener onClickAway={() => setShowTextColors(false)}>
          <div className="inapp-rt-color-wrapper">
            <IconButton
              size="small"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setShowBackgroundColors(false);
                setShowTextColors((open) => !open);
              }}
              title="Text color"
              aria-label="Text color"
            >
              <span className="inapp-rt-color-btn">
                <FormatColorTextIcon fontSize="small" className="inapp-rt-color-btn-icon" />
                <span
                  className="inapp-rt-color-btn-bar"
                  style={{
                    backgroundColor: textColor,
                    border: textColor.toLowerCase() === "#ffffff" ? "1px solid #ccc" : "none",
                  }}
                />
              </span>
            </IconButton>
            {showTextColors && (
              <div className="inapp-rt-color-palette">
                {TEXT_COLORS.map((color) => (
                  <div
                    key={color}
                    className="inapp-rt-color-swatch"
                    style={{ backgroundColor: color }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyTextColor(color)}
                    title={color}
                    role="button"
                    tabIndex={0}
                  />
                ))}
              </div>
            )}
          </div>
        </ClickAwayListener>
        <ClickAwayListener onClickAway={() => setShowBackgroundColors(false)}>
          <div className="inapp-rt-color-wrapper">
            <IconButton
              size="small"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setShowTextColors(false);
                setShowBackgroundColors((open) => !open);
              }}
              title="Background color"
              aria-label="Background color"
            >
              <span className="inapp-rt-color-btn">
                <FormatColorFillIcon fontSize="small" className="inapp-rt-color-btn-icon" />
                <span
                  className="inapp-rt-color-btn-bar"
                  style={{
                    backgroundColor: backgroundColor,
                    border: backgroundColor.toLowerCase() === "#ffffff" ? "1px solid #ccc" : "none",
                  }}
                />
              </span>
            </IconButton>
            {showBackgroundColors && (
              <div className="inapp-rt-color-palette">
                {BACKGROUND_COLORS.map((color) => (
                  <div
                    key={color}
                    className="inapp-rt-color-swatch"
                    style={{
                      backgroundColor: color,
                      border: color === "#FFFFFF" ? "1px solid #ccc" : "none",
                    }}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyBackgroundColor(color)}
                    title={color}
                    role="button"
                    tabIndex={0}
                  />
                ))}
              </div>
            )}
          </div>
        </ClickAwayListener>
        <div className="inapp-rt-toolbar-divider" />
        <div className="inapp-rt-font-size-wrapper">
          <FormatSizeIcon fontSize="small" style={{ marginRight: 4, color: "#555" }} />
          <Select
            value={fontSize}
            onChange={(event) => applyFontSize(event.target.value)}
            onMouseDown={(event) => event.preventDefault()}
            variant="standard"
            disableUnderline
            className="inapp-rt-font-size-select"
            aria-label="Font size"
          >
            {FONT_SIZES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="inapp-rt-toolbar-divider" />
        <IconButton
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={applyRemoveFormatting}
          title="Remove formatting"
          aria-label="Remove formatting"
        >
          <FormatClearIcon fontSize="small" />
        </IconButton>
      </div>
      <div
        ref={editorRef}
        id={id}
        className="inapp-rt-editor"
        contentEditable
        suppressContentEditableWarning
        style={{ minHeight: editorMinHeight }}
        data-placeholder={placeholder}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onInput={emitChange}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default InAppRichTextEditor;
